/**
 * <heritage-rich-nav>
 *
 * Single coordinator for the header's rich-menu dropdowns. Triggers and
 * panels live as siblings inside this element; the component owns the
 * open/close state and ensures only one panel is open at a time.
 *
 * Markup contract:
 *   <heritage-rich-nav>
 *     <button data-rich-nav-trigger="catalog" aria-expanded="false" aria-haspopup="true">…</button>
 *     <button data-rich-nav-trigger="memo" …>…</button>
 *     …
 *     <div data-rich-nav-panel="catalog" hidden>…</div>
 *     <div data-rich-nav-panel="memo"    hidden>…</div>
 *     …
 *   </heritage-rich-nav>
 *
 * Trigger ↔ panel pairing is by matching id (data-rich-nav-trigger value
 * equals data-rich-nav-panel value).
 *
 * Behavior:
 *   - Mouse hover on trigger → open after 200ms intent delay
 *   - Mouse leave trigger or panel → close after 150ms grace
 *   - Mouse enter panel → cancel pending close (allows traversal)
 *   - Click trigger → toggle immediately (cancels any timers)
 *   - Enter / Space on focused trigger → toggle
 *   - ArrowDown on focused trigger → open + focus first link in panel
 *   - Escape (anywhere within nav) → close all, return focus to active trigger
 *   - Click outside the component → close all
 *   - Tab leaves all of the open panel's focusables → close
 *
 * Communication: dispatches `heritage:rich-nav:open` / `:close` custom
 * events on itself (bubbles) with detail `{ id }` for downstream listeners.
 */

const HOVER_INTENT_OPEN_MS = 200;
const HOVER_INTENT_CLOSE_MS = 150;

class HeritageRichNav extends HTMLElement {
  connectedCallback() {
    this._triggers = Array.from(this.querySelectorAll('[data-rich-nav-trigger]'));
    this._panels = Array.from(this.querySelectorAll('[data-rich-nav-panel]'));
    if (this._triggers.length === 0) return;

    this._openId = null;
    this._openTimer = null;
    this._closeTimer = null;

    this._panelById = Object.fromEntries(this._panels.map((p) => [p.dataset.richNavPanel, p]));
    this._triggerById = Object.fromEntries(this._triggers.map((t) => [t.dataset.richNavTrigger, t]));

    // Initial state: hidden + aria
    this._panels.forEach((p) => { p.hidden = true; p.dataset.state = 'closed'; });
    this._triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));

    // Trigger handlers
    this._triggers.forEach((t) => {
      const id = t.dataset.richNavTrigger;
      t.addEventListener('mouseenter', () => this._scheduleOpen(id));
      t.addEventListener('mouseleave', (e) => {
        // Keep the panel open as long as the cursor is anywhere within
        // the rich-nav region (header internals, sibling triggers, the
        // gaps between elements, the panels themselves). Only close when
        // the cursor genuinely exits the nav.
        //
        // Why this matters: the browser fires a spurious mouseleave on
        // the trigger right after the panel opens — the hover target
        // re-evaluates and reports the cursor as being on the trigger's
        // parent (heritage-header__inner) even though the cursor hasn't
        // moved. relatedTarget for that event is the parent grid, not
        // the panel — so a panel-only check isn't enough.
        if (e.relatedTarget && this.contains(e.relatedTarget)) return;
        this._scheduleClose();
      });
      t.addEventListener('focus', () => {
        // Don't auto-open on focus to avoid flashing during keyboard traversal.
        // ArrowDown will open + focus first link.
      });
      t.addEventListener('click', (e) => {
        // If the trigger is an anchor with a real URL, let the click
        // navigate naturally — parent menu labels are clickable
        // destinations alongside the hover-to-open dropdown.
        if (t.tagName === 'A') {
          var href = t.getAttribute('href');
          if (href && href !== '#' && href !== '/') return;
        }
        e.preventDefault();
        this._cancelTimers();
        if (this._openId === id) this._closeAll();
        else this._open(id);
      });
      t.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._cancelTimers();
          if (this._openId === id) this._closeAll();
          else this._open(id);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          this._cancelTimers();
          this._open(id);
          requestAnimationFrame(() => this._focusFirstInPanel(id));
        } else if (e.key === 'Escape') {
          if (this._openId !== null) {
            e.preventDefault();
            this._closeAll();
          }
        }
      });
    });

    // Panel handlers — keep open while cursor is inside, close when it leaves
    this._panels.forEach((p) => {
      p.addEventListener('mouseenter', () => this._cancelTimers());
      p.addEventListener('mouseleave', (e) => {
        // Same rule as the trigger: stay open while the cursor is
        // anywhere inside the rich-nav region.
        if (e.relatedTarget && this.contains(e.relatedTarget)) return;
        this._scheduleClose();
      });
      p.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          const id = p.dataset.richNavPanel;
          this._closeAll();
          const trigger = this._triggerById[id];
          if (trigger) trigger.focus();
        }
      });
      // Tab past last focusable closes
      p.addEventListener('focusout', (e) => {
        // Defer so document.activeElement settles
        requestAnimationFrame(() => {
          if (this._openId !== p.dataset.richNavPanel) return;
          if (!p.contains(document.activeElement) && document.activeElement !== this._triggerById[p.dataset.richNavPanel]) {
            this._closeAll({ skipFocus: true });
          }
        });
      });
    });

    // Click outside closes
    this._onDocClick = (e) => {
      if (this._openId === null) return;
      if (!this.contains(e.target)) this._closeAll({ skipFocus: true });
    };
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    this._cancelTimers();
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
  }

  _scheduleOpen(id) {
    this._cancelTimers();
    if (this._openId === id) return; // already open
    if (this._openId !== null) {
      // Switching panels while another is open — open immediately for crossfade
      this._open(id);
      return;
    }
    this._openTimer = setTimeout(() => this._open(id), HOVER_INTENT_OPEN_MS);
  }

  _scheduleClose() {
    if (this._openTimer) { clearTimeout(this._openTimer); this._openTimer = null; }
    if (this._openId === null) return;
    if (this._closeTimer) clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => this._closeAll({ skipFocus: true }), HOVER_INTENT_CLOSE_MS);
  }

  _cancelTimers() {
    if (this._openTimer) { clearTimeout(this._openTimer); this._openTimer = null; }
    if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; }
  }

  _open(id) {
    const panel = this._panelById[id];
    const trigger = this._triggerById[id];
    if (!panel || !trigger) return;

    // Close previously open panel (if any)
    if (this._openId !== null && this._openId !== id) {
      const prev = this._panelById[this._openId];
      const prevTrigger = this._triggerById[this._openId];
      if (prev) { prev.hidden = true; prev.dataset.state = 'closed'; }
      if (prevTrigger) prevTrigger.setAttribute('aria-expanded', 'false');
    }

    panel.hidden = false;
    // Force reflow so the state transition fires
    void panel.offsetWidth;
    panel.dataset.state = 'open';
    trigger.setAttribute('aria-expanded', 'true');
    this._openId = id;

    this.dispatchEvent(new CustomEvent('heritage:rich-nav:open', {
      bubbles: true,
      detail: { id },
    }));
  }

  _closeAll(opts) {
    if (this._openId === null) return;
    const id = this._openId;
    const panel = this._panelById[id];
    const trigger = this._triggerById[id];
    if (panel) { panel.hidden = true; panel.dataset.state = 'closed'; }
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (!opts || !opts.skipFocus) {
        // restore focus only if explicit close (e.g., Escape pressed)
        trigger.focus({ preventScroll: true });
      }
    }
    this._openId = null;

    this.dispatchEvent(new CustomEvent('heritage:rich-nav:close', {
      bubbles: true,
      detail: { id },
    }));
  }

  _focusFirstInPanel(id) {
    const panel = this._panelById[id];
    if (!panel) return;
    const focusable = panel.querySelector(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) focusable.focus({ preventScroll: true });
  }
}

if (!customElements.get('heritage-rich-nav')) {
  customElements.define('heritage-rich-nav', HeritageRichNav);
}
